import { Component,ContentChild,EventEmitter,Input,Output, SimpleChanges } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { DialogService } from '@app/old-ui/dialog/dialog.service';
import { WebClient } from '@injectables';
import { DateTime, ModelMeta,WhereTable } from '@lib/model-meta';
import { BehaviorSubject,combineLatest,firstValueFrom,Observable,of,op } from '@lib/rxjs';
import { ModelTableActionsDirective } from './model-table-actions.directive';
import { ModelTableHeaderDirective } from './model-table-header.directive';
import { ModelTableRowDirective } from './model-table-row.directive';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { convertFiltersToWhereTable, Filter, FilterConfig, filterTypes, ModalFilter, createSearchWhereTable } from './model-table-filter';
import { measurement } from '@lib/measurement';
import { User } from '@models';

@Component({
	selector: 'model-table',
	templateUrl: './model-table.component.html',
	styleUrls: ['./model-table.component.scss']
})
export class ModelTableComponent<
	Instance extends {
		id:number;
		name?:string;
	},
	RestTypes extends WebClient.RestTypes,
>{
	constructor(
		protected readonly dialog:DialogService,
		protected readonly fragQuery:FragQueryService,
		private readonly user: User,		
	){
		this.filterConfigs$.pipe(
			op.map((filterConfigs) => {
			  return filterConfigs.length > 0 ? filterConfigs[0] : null;
			})
		  ).subscribe((selectedColumn) => {
			this.selectedColumn = selectedColumn;
		  });
	}
	public readonly particleSizeUnitSystem$ = this.fragQuery.particleSizeUnitSystem$;
	public readonly quantityUnitSystem$ = this.fragQuery.QuantityUnitSystem$;

	@Input() title:string;
	@Input() model:{
		meta:ModelMeta;
		new ():Instance;
	};
	@Input() 
		get where(){
			return this.where$.value;
		}
		set where(v){
			this.where$.next(v);
		}
	
	@Input()
		get filterConfigs(){
			return this.filterConfigs$.value;
		}
		set filterConfigs(v){
			this.filterConfigs$.next(v);
		}

	@Input() enableDefaultConfigs = false;
	
	@Input() 
	get filter() {
		return this.search$.value;
	}
	set filter(v) {
		this.search$.next(v);
	}

	get selectedFilterValueAsDate(): DateTime | null {
		return typeof this.selectedFilterValue === 'string' ? null : this.selectedFilterValue;
	}
	set selectedFilterValueAsDate(value: DateTime | null | string) {
		this.selectedFilterValue = value ?? '';
	}

	@Input() sort:Record<string,'+'|'-'>;
	@Input() restClient:WebClient.RestModel<Instance,RestTypes>;
	@Input() editDialog:{
		new(...args:any[]):any;
		config:MatDialogConfig<{record:Instance,showTimes?:Boolean}>;
		output:boolean;
	};
	@Input() deletable=(v:Instance)=>true;
	@Input() addable=(v?:Instance)=>true;

	public filterConfigs$ = new BehaviorSubject<any[]>([]);
	public selectedColumn:any = ''; // Using 'any' to allow different column configurations (e.g., string, object)
	public selectedFilterType:string = 'Choose filter..';
	public selectedFilterValue : string | DateTime
	public filters: FilterConfig[] = [];

	@Output('add') addEmitter=new EventEmitter<((confirm:boolean|PromiseLike<boolean>)=>void)>();
	@Output('openDefaultConfigsDialog') openDefaultConfigsEmitter=new EventEmitter<void>();

	@ContentChild(ModelTableHeaderDirective) headerContent:ModelTableHeaderDirective;
	@ContentChild(ModelTableRowDirective) rowContent:ModelTableRowDirective<Instance>;
	@ContentChild(ModelTableActionsDirective) actionsContent:ModelTableActionsDirective<Instance>;

	public resetFilterValues() {
		if (this.selectedColumn.type === 'select' || this.selectedColumn.type === 'input' || this.selectedColumn.type === 'date') {
			this.selectedFilterValue = '';
			this.selectedFilterValueAsDate = null;
			this.selectedFilterType = ""
		}
	}

	public readonly where$=new BehaviorSubject<WhereTable>({});
	public readonly filterWhere$=new BehaviorSubject<WhereTable>({});
	public readonly search$=new BehaviorSubject<Filter[]>([]);
	public readonly pageIndex$=new BehaviorSubject(0);
	public readonly pageSize$=new BehaviorSubject(25);

	public readonly selected=new Set<Instance>();
	
	public openAddDefaultConfigs() {
        this.openDefaultConfigsEmitter.emit();
    }

  	public readonly recordCount$=combineLatest([
		this.where$,
		this.filterWhere$,
		this.search$
	])
		.pipe(
    		op.switchMap(([where, filterWhere, searchs])=>{
				const searchWhere = createSearchWhereTable(searchs);
				const mergedWhere: WhereTable = { ...where, ...filterWhere, ...searchWhere };
				return this.restClient.count$({ where: mergedWhere })
			}),
    		op.startWith(undefined),
    		op.shareReplay(1)
		);

	public readonly pageRecords$=
		combineLatest([
			this.pageIndex$.pipe(op.distinctUntilChanged()),
			this.pageSize$.pipe(op.distinctUntilChanged()),
			this.where$,
			this.filterWhere$,
			this.search$
		])
		.pipe(
			op.debounceTime(250),
			op.switchMap(([pageIndex,pageSize,where,filterWhere,searchs])=>{
				const searchWhere = createSearchWhereTable(searchs);
				const mergedWhere: WhereTable = { ...where, ...filterWhere, ...searchWhere };
				return this.restClient.search$({
					where: mergedWhere,
					range: [pageIndex*pageSize,pageSize],
					sort: this.sort
				}).pipe(op.startWith(undefined));
			}),
			op.startWith(undefined),
			op.shareReplay(1)
		);

	public getFilterTypeValues(columnType: string) {
		if (columnType.startsWith('measurement-')) {
			// Find and return the corresponding measurement filter from filterTypes
			return filterTypes.find(filterType => 
				filterType.type === 'measurement'
			);
		} else {
			return filterTypes.find(filterType => 
				filterType.type === columnType
			);
		}
	}

	public getNumericValue(value: string | number | DateTime): number | number[] | null {
		if (typeof value === 'number') {
		  return value;
		}
		return null;
	}	  
	  

	public addFilter(addfilter: ModalFilter, type:string, value: string | DateTime | number) {
        const filterObj : FilterConfig = {
			column: addfilter.column,
			type,
			value,
			dbColumn: this.filterConfigs.find(filter => filter.column === addfilter.column).dbColumn,
			spec: addfilter.type,
			unitSystem$: addfilter.unitSystem$
		}

		if(addfilter.isValueLabelPair){
			filterObj.label = this.filterConfigs.find(filter => filter.column === addfilter.column).options.find((opt: { value: string | DateTime; }) => opt.value === value).label
		}

		this.filters.push(filterObj);
		const whereTable = convertFiltersToWhereTable(this.filters);
		this.filterWhere$.next(whereTable);
	}

	public isValueLabelPairSelectColumn(column: ModalFilter): boolean {
		return column.isValueLabelPair ?? false;
	}

	public getMeasurementFilterInfo(column: ModalFilter): { isMeasurement: boolean; measurementType: measurement.Type} {
		const match = column.type.match(/^measurement-([a-zA-Z]+)$/); // Match any word after 'measurement-'

		return {
			isMeasurement: !!match,
			measurementType: match ? match[1] as measurement.Type : undefined,
		};
	}

	public getMeasurementTagInfo(tag: FilterConfig): { isMeasurement: boolean; measurementType: measurement.Type} {
		const match = tag.spec.match(/^measurement-([a-zA-Z]+)$/); // Match any word after 'measurement-'

		return {
			isMeasurement: !!match,
			measurementType: match ? match[1] as measurement.Type : undefined,
		};
	}

	public removeFilter(filter: FilterConfig): void {
		const index = this.filters.indexOf(filter);
		if (index < 0) {
			return;
		}
	
		this.filters.splice(index, 1);
		const whereTable = convertFiltersToWhereTable(this.filters);
		this.filterWhere$.next(whereTable)
	}
	
	public async selectAll(v:boolean){
		if(v){
			const records=await firstValueFrom(this.pageRecords$);
			for(const record of records)
				this.selected.add(record);
		}else{
			this.selected.clear();
		}
	}

	public onPage(evt:PageEvent){
		this.selected.clear();
		this.pageIndex$.next(evt.pageIndex);
		this.pageSize$.next(evt.pageSize);
	}

	public updateRecords(){
		this.where$.next(this.where$.value);
	}

	public async add(){
		if(this.addEmitter.observed){
			if(await new Promise<boolean>(resolve=>this.addEmitter.emit(resolve))){
				this.updateRecords();
			}
		}else{
			const record=new this.model();
			if(await this.dialog.open(this.editDialog,{record})){
				await this.dialog.await(this.restClient.save(record));
				this.updateRecords();
			}
		}
	}

	public async edit(record:Instance){
		if(await this.dialog.open(this.editDialog,{record})){
			await this.dialog.await(this.restClient.save(record));
			this.fragQuery.trigger$.next();
			this.updateRecords();
			if (this.model.meta.name === 'User' && this.user.id === record.id) {
                this.fragQuery.userSettingUpdated$.next();
            }
		}
	}

	public async _delete(record?:Instance){
		if(record){ 
			if(await this.dialog.confirm({message:'Are you sure you want to delete '+(record.name ?? 'this')})){
				await this.dialog.await(this.restClient.delete(record));
				this.updateRecords();
			}
		}else{
			if(await this.dialog.confirm({message:'Are you sure you want to delete the selected records'})){
				await this.dialog.await((async ()=>{
					for(const record of this.selected){
						if(this.deletable(record))
							await this.restClient.delete(record);
					}
				})());
				this.updateRecords();
			}
		}
		
	}
}